import { useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { AlertTriangle, Send } from "lucide-react";
import { toast } from "../../lib/toast";
import { computeSegments, type DevicePublic } from "@smsbridge/shared";
import { Badge } from "../../components/ui/Badge";
import { DatePicker } from "../../components/ui/DatePicker";
import { Select } from "../../components/ui/Select";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { Modal } from "../../components/ui/Modal";
import { api, apiErrorMessage } from "../../lib/api";
import { RecipientInput, type Recipient } from "./RecipientInput";

/** Above this many recipients, a mis-click is expensive enough to warrant a confirm step. */
const CONFIRM_THRESHOLD = 50;
const MAX_BODY = 1600;
const ONLINE_WINDOW_MS = 3 * 60 * 1000;

function isOnline(device: DevicePublic): boolean {
  if (!device.isEnabled || !device.lastHeartbeatAt) return false;
  return Date.now() - new Date(device.lastHeartbeatAt).getTime() < ONLINE_WINDOW_MS;
}

interface SendResponse {
  batchId: string | null;
  accepted: number;
  rejected: Array<{ recipient: string; reason: string }>;
}

export function ComposeForm() {
  const queryClient = useQueryClient();

  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [body, setBody] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [simSlot, setSimSlot] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const devicesQuery = useQuery({
    queryKey: ["devices"],
    queryFn: async () => (await api.get<DevicePublic[]>("/devices")).data,
  });

  const devices = devicesQuery.data ?? [];
  const hasDevice = devices.length > 0;

  const valid = useMemo(() => recipients.filter((r) => r.e164), [recipients]);
  // Reuses the API's own segment logic from @smsbridge/shared, so the counter can't disagree
  // with what the server records.
  const { encoding, segments } = useMemo(() => computeSegments(body || " "), [body]);

  const selectedDevice = devices.find((d) => d._id === deviceId);
  const simSlots = selectedDevice?.simSlots ?? [];

  const sendMutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {
        recipients: valid.map((r) => r.e164),
        message: body,
      };
      if (deviceId) payload.deviceId = deviceId;
      if (simSlot !== "") payload.simSlot = Number(simSlot);
      if (scheduledAt) payload.scheduledAt = new Date(scheduledAt).toISOString();
      return (await api.post<SendResponse>("/gateway/send-sms", payload)).data;
    },
    onSuccess: (result) => {
      toast.success(
        `${result.accepted} message${result.accepted === 1 ? "" : "s"} accepted${
          result.rejected.length ? `, ${result.rejected.length} rejected` : ""
        }.`,
      );
      for (const rejection of result.rejected) {
        toast.warning(`${rejection.recipient}: ${rejection.reason}`);
      }
      setRecipients([]);
      setBody("");
      setScheduledAt("");
      void queryClient.invalidateQueries({ queryKey: ["messages"] });
      void queryClient.invalidateQueries({ queryKey: ["stats"] });
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Could not send.")),
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (valid.length === 0 || body.trim() === "") return;
    if (valid.length > CONFIRM_THRESHOLD) {
      setConfirmOpen(true);
      return;
    }
    sendMutation.mutate();
  }

  return (
    <>
      {!devicesQuery.isLoading && !hasDevice && (
        <Card className="mb-4 flex items-start gap-3 border-warn/40 bg-warn-soft">
          <AlertTriangle className="mt-0.5 size-5 shrink-0 text-warn" aria-hidden />
          <div className="text-sm">
            <p className="font-medium text-text">No device paired</p>
            <p className="mt-0.5 text-text-muted">
              Messages can&apos;t be sent until you connect an Android phone. You can still compose
              one — it will be recorded and marked failed with the reason.{" "}
              <Link to="/devices" className="font-medium text-accent hover:underline">
                Pair a device
              </Link>
            </p>
          </div>
        </Card>
      )}

      <form onSubmit={submit} className="flex flex-col gap-5">
        <RecipientInput value={recipients} onChange={setRecipients} />

        <div className="flex flex-col gap-1.5">
          <label htmlFor="compose-body" className="text-sm font-medium text-text">
            Message
          </label>
          <textarea
            id="compose-body"
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, MAX_BODY))}
            rows={6}
            placeholder="Type your message…"
            className="min-h-[8.5rem] resize-y rounded-control border border-border bg-surface px-3 py-2 text-sm text-text outline-none transition-colors duration-fast ease-standard placeholder:text-text-muted focus:border-accent focus:ring-2 focus:ring-accent/20"
          />
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-control border border-border/60 bg-surface/60 px-3 py-2 text-xs text-text-muted">
            <span className="tnum">
              <span className="font-medium text-text">{body.length}</span> / {MAX_BODY} characters
            </span>
            <span aria-hidden className="text-border">|</span>
            <span className="tnum">
              <span className="font-medium text-text">{segments}</span> segment
              {segments === 1 ? "" : "s"}
            </span>
            <Badge
              tone={encoding === "GSM7" ? "neutral" : "warn"}
              size="sm"
              className="font-mono text-[11px]"
            >
              {encoding}
            </Badge>
            {encoding === "UCS2" && (
              <span className="text-warn">
                Unicode characters cut the per-segment limit from 160 to 70.
              </span>
            )}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <Select
            id="compose-device"
            label="Device"
            value={deviceId}
            onChange={(e) => {
              setDeviceId(e.target.value);
              setSimSlot("");
            }}
          >
            <option value="">Automatic (default device)</option>
            {devices.map((device) => (
              <option key={device._id} value={device._id}>
                {device.name} — {isOnline(device) ? "online" : "offline"}
              </option>
            ))}
          </Select>

          <Select
            id="compose-sim"
            label="SIM slot"
            value={simSlot}
            onChange={(e) => setSimSlot(e.target.value)}
            disabled={simSlots.length === 0}
          >
            <option value="">Device default</option>
            {simSlots.map((slot) => (
              <option key={slot.slot} value={slot.slot}>
                Slot {slot.slot} — {slot.carrier}
              </option>
            ))}
          </Select>

          <DatePicker
            id="compose-schedule"
            mode="datetime"
            label="Send at"
            placeholder="Send now"
            value={scheduledAt}
            onChange={setScheduledAt}
          />
        </div>

        <div className="flex items-center gap-3">
          <Button
            type="submit"
            size="lg"
            loading={sendMutation.isPending}
            disabled={valid.length === 0 || body.trim() === ""}
            leftIcon={<Send className="size-4" aria-hidden />}
          >
            {scheduledAt ? "Schedule" : "Send"}
            {valid.length > 0 ? ` to ${valid.length}` : ""}
          </Button>
          {valid.length > 0 && (
            <span className="text-xs text-text-muted">
              {valid.length * segments} SMS segment{valid.length * segments === 1 ? "" : "s"} will
              be used.
            </span>
          )}
        </div>
      </form>

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={`Send to ${valid.length} recipients?`}
        description={`This consumes ${valid.length * segments} SMS segments from your quota and cannot be undone once the phone starts sending.`}
        size="sm"
      >
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            loading={sendMutation.isPending}
            onClick={() => {
              setConfirmOpen(false);
              sendMutation.mutate();
            }}
          >
            Send anyway
          </Button>
        </div>
      </Modal>
    </>
  );
}
